import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Radio,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { usePromptText } from "modules/shared/components/accessibility/PromptStore";
import { useProjectDetailsStore } from "modules/organization/store";
import { useRankingProjectListStore } from "modules/organization/store/rankingProjectListStore";
import { RankingProjectService } from "modules/organization/services/rankingProject.service";
import {
  ICreateEditRankingProjectPayload,
  IRankingProjectRank,
} from "modules/organization/models/interface";
import { DateType, mnDate } from "modules/shared/services";
import { dateFormat } from "config/commonConfig";

const { RangePicker } = DatePicker;

interface IRankingProjectFormData {
  title?: string;
  ranking_type_id?: number;
  ranking_template_id?: number;
  include_all_records?: boolean;
  date_range?: any;
  ranks?: IRankingProjectRank[];
}

const CreateEditRankingProjectForm: React.FC<{ visibility: boolean }> = ({
  visibility,
}) => {
  const [form] = Form.useForm<IRankingProjectFormData>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Pristine rank list, used to diff against Form.List's current state and find removed ranks.
  const [originalRanks, setOriginalRanks] = useState<IRankingProjectRank[]>([]);
  const [rankingTypesLoading, setRankingTypesLoading] =
    useState<boolean>(false);
  const [rankingTemplateOptionsLoading, setRankingTemplateOptionsLoading] =
    useState<boolean>(false);
  const [dateRangeBoundsLoading, setDateRangeBoundsLoading] =
    useState<boolean>(false);
  const { setPromptType, setPromptText } = usePromptText();
  const watchRankingTypeId = Form.useWatch("ranking_type_id", form);
  const watchRankingTemplateId = Form.useWatch("ranking_template_id", form);
  const watchRanks = Form.useWatch("ranks", form);

  const { projectDetails } = useProjectDetailsStore();
  const {
    rankingProjectDrawerState,
    rankingProjectDrawerOptions,
    setRankingProjectDrawerState,
    refreshRankingProjectList,
    rankingProjects,
    rankingTypes,
    rankingTemplateOptions,
    getRankingTypes,
    getRankingTemplateOptions,
    projectRecordDateRange,
    getProjectRecordDateRange,
  } = useRankingProjectListStore();

  const rankingProjectInfo = rankingProjectDrawerOptions.data;
  // FR-011: locked once the project has an execution recorded.
  const isLocked = !!rankingProjectInfo?.last_execution;
  const isEdit = !!rankingProjectDrawerOptions.key;
  // Only true on the first fetch ever — a later background re-fetch shouldn't blank existing data.
  const isFirstTemplateOptionsLoad =
    rankingTemplateOptionsLoading && rankingTemplateOptions === null;
  // True when the stored ranking_template_id no longer matches any option — the template was deleted.
  const isDeletedTemplateReference =
    !isFirstTemplateOptionsLoad &&
    watchRankingTemplateId != null &&
    !rankingTemplateOptions?.some(
      (eachTemplate) => eachTemplate.id === watchRankingTemplateId,
    );

  // project_slug is only needed for the permission guard, not the query itself.
  useEffect(() => {
    if (projectDetails?.slug) {
      setRankingTypesLoading(true);
      getRankingTypes(projectDetails.slug)
        .catch((error) => console.error(error))
        .finally(() => setRankingTypesLoading(false));
    }
  }, [projectDetails?.slug, getRankingTypes]);

  // Bounds the Date Range picker to the project's own record dates (fetch-once-cached,
  // matching getRankingTypes/getRankingTemplateOptions's convention).
  useEffect(() => {
    if (projectDetails?.slug) {
      setDateRangeBoundsLoading(true);
      getProjectRecordDateRange(projectDetails.slug)
        .catch((error) => console.error(error))
        .finally(() => setDateRangeBoundsLoading(false));
    }
  }, [projectDetails?.slug, getProjectRecordDateRange]);

  // Disables any date outside the project's own record date range — mirrors
  // CodingReviewFilters.tsx's disabledDate pattern (mnDate(...).date comparison).
  const disabledDate = (date: DateType) => {
    const minDate = projectRecordDateRange?.min_effective_from;
    const maxDate = projectRecordDateRange?.max_through_to;
    if (!minDate || !maxDate) {
      return false;
    }
    return date < mnDate(minDate).date || date > mnDate(maxDate).date;
  };

  const dateRangeDefaultPickerValue = projectRecordDateRange?.min_effective_from
    ? ([
        mnDate(projectRecordDateRange.min_effective_from).date,
        mnDate(projectRecordDateRange.min_effective_from).date,
      ] as [DateType, DateType])
    : undefined;

  useEffect(() => {
    if (watchRankingTypeId === 1 && projectDetails?.slug) {
      setRankingTemplateOptionsLoading(true);
      getRankingTemplateOptions(projectDetails.slug)
        .catch((error) => console.error(error))
        .finally(() => setRankingTemplateOptionsLoading(false));
    }
  }, [watchRankingTypeId, projectDetails?.slug, getRankingTemplateOptions]);

  useEffect(() => {
    if (!visibility) {
      form.resetFields();
      setOriginalRanks([]);
    }
  }, [visibility, form]);

  useEffect(() => {
    if (rankingProjectInfo && rankingProjectDrawerState) {
      const existingRanks = rankingProjectInfo.ranks ?? [];
      form.setFieldsValue({
        title: rankingProjectInfo.title,
        ranking_type_id: rankingProjectInfo.ranking_type_id,
        ranking_template_id:
          rankingProjectInfo.ranking_template_id ?? undefined,
        include_all_records: rankingProjectInfo.include_all_records,
        // RangePicker needs a raw dayjs instance — mnDate()'s .date property, not the wrapper itself.
        date_range:
          rankingProjectInfo.effective_from && rankingProjectInfo.through_to
            ? [
                mnDate(rankingProjectInfo.effective_from).date,
                mnDate(rankingProjectInfo.through_to).date,
              ]
            : null,
        ranks: existingRanks,
      });
      setOriginalRanks(existingRanks);
    }
  }, [rankingProjectDrawerState, rankingProjectInfo, form]);

  // FR-003: title uniqueness is scoped per-project, not organization-wide.
  const validateUniqueProjectTitle = (_: any, value: string) => {
    const duplicate = rankingProjects?.some(
      (project) =>
        project.id !== rankingProjectInfo?.id &&
        project.title?.toLowerCase() === value?.toLowerCase(),
    );
    if (duplicate) {
      return Promise.reject(
        new Error("A ranking project with this name already exists."),
      );
    }
    return Promise.resolve();
  };

  // FR-007: copies the template's ranks in; never writes back to the source template.
  const onTemplateSelect = (templateId: number) => {
    const template = rankingTemplateOptions?.find(
      (eachTemplate) => eachTemplate.id === templateId,
    );
    const copiedRanks: IRankingProjectRank[] = (template?.ranks ?? []).map(
      (rank) => ({
        rank_name: rank.rank_name ?? "",
        display_order: rank.display_order ?? 0,
        rank_icon_url: rank.rank_icon_url ?? null,
      }),
    );
    form.setFieldValue("ranks", copiedRanks);
    setOriginalRanks(copiedRanks);
  };

  // FR-004/FR-005 — Include All Records and a Date Range are mutually exclusive.
  const onIncludeAllRecordsChange = (checked: boolean) => {
    if (checked) {
      form.setFieldValue("date_range", null);
    }
  };

  const onDateRangeChange = (dates: any) => {
    if (dates) {
      form.setFieldValue("include_all_records", false);
    }
  };

  const onFinish = async (values: IRankingProjectFormData) => {
    if (!projectDetails?.slug) {
      return;
    }
    const createRankingProjectKey = "createEditRankingProject";
    const promptText = rankingProjectDrawerOptions.toastMessage
      ?.loading as string;
    setPromptType("assertive");
    setPromptText(promptText);
    setIsSubmitting(true);
    message.open({
      key: createRankingProjectKey,
      content: promptText,
      type: "loading",
      duration: 0,
    });

    // Backend sources rank data itself; only report which display_order values were removed.
    const remainingOrders = new Set(
      (values.ranks ?? []).map((rank) => rank.display_order),
    );
    const removedRankOrders = originalRanks
      .filter((rank) => !remainingOrders.has(rank.display_order))
      .map((rank) => rank.display_order);

    const payload: ICreateEditRankingProjectPayload = {
      id: isEdit ? rankingProjectInfo?.id : undefined,
      project_slug: projectDetails.slug,
      title: values.title as string,
      ranking_type_id: values.ranking_type_id as number,
      ranking_template_id: values.ranking_template_id,
      include_all_records: !!values.include_all_records,
      effective_from: values.date_range?.[0]
        ? mnDate(values.date_range[0]).format(dateFormat.database)
        : undefined,
      through_to: values.date_range?.[1]
        ? mnDate(values.date_range[1]).format(dateFormat.database)
        : undefined,
      removed_rank_orders: removedRankOrders,
    };

    try {
      isEdit
        ? await new RankingProjectService().editRankingProject(
            projectDetails.slug,
            payload,
          )
        : await new RankingProjectService().createRankingProject(
            projectDetails.slug,
            payload,
          );
      setRankingProjectDrawerState(false);
      await refreshRankingProjectList(projectDetails.slug);
      setPromptType("assertive");
      setPromptText(
        rankingProjectDrawerOptions.toastMessage?.success as string,
      );
      message.success({
        key: createRankingProjectKey,
        content: rankingProjectDrawerOptions.toastMessage?.success,
        duration: 2,
      });
    } catch (error: any) {
      console.error(error);
      setIsSubmitting(false);
      const backendMessage = error?.error?.response?.data?.message;
      const errorContent =
        backendMessage || rankingProjectDrawerOptions.toastMessage?.error;
      setPromptType("assertive");
      setPromptText(errorContent as string);
      message.error({
        key: createRankingProjectKey,
        content: errorContent,
      });
    }
  };

  return (
    <Form
      form={form}
      disabled={isSubmitting}
      layout="vertical"
      onFinish={(props) => {
        onFinish(props).catch(console.error);
      }}
      autoComplete="off"
      className="drawerContent"
      initialValues={{
        ranking_type_id: 1,
        include_all_records: true,
        ranks: [],
      }}
      name="form"
    >
      <div className="blkContent">
        <Form.Item
          name="title"
          label="Project Title"
          validateFirst
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Project Title is required",
            },
            { validator: validateUniqueProjectTitle },
          ]}
        >
          {/* FR-011: stays editable even when isLocked. */}
          <Input
            aria-label="Enter Ranking Project Title"
            placeholder="Enter ranking project title"
            maxLength={100}
            autoFocus
          />
        </Form.Item>

        <Form.Item
          label="Record Selection"
          className="recordSelectionFldWrap noFocusWithin"
          required
        >
          <Space align="baseline">
            {/* FR-005: at least one of date_range/include_all_records must be set. */}
            <Form.Item
              name="date_range"
              dependencies={["include_all_records"]}
              rules={[
                {
                  validator: (_, value) => {
                    const includeAll = form.getFieldValue(
                      "include_all_records",
                    );
                    if (!includeAll && (!value || !value[0] || !value[1])) {
                      return Promise.reject(
                        new Error("Select either All Records or a Date Range."),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              {dateRangeBoundsLoading ? (
                <Skeleton.Input active size="default" style={{ width: 250 }} />
              ) : (
                <RangePicker
                  format={dateFormat.default}
                  disabled={isLocked}
                  onChange={onDateRangeChange}
                  disabledDate={disabledDate}
                  defaultPickerValue={dateRangeDefaultPickerValue}
                />
              )}
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {() => {
                const hasError = form.getFieldError("date_range").length > 0;
                return (
                  <Form.Item
                    name="include_all_records"
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox
                      disabled={isLocked}
                      className={hasError ? "checkboxError" : undefined}
                      onChange={(e) =>
                        onIncludeAllRecordsChange(e.target.checked)
                      }
                    >
                      Include all records
                    </Checkbox>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Space>
        </Form.Item>

        {/* Stays mounted — Select Template below watches this field's value. */}
        <Form.Item
          name="ranking_type_id"
          label="Select Ranking Type"
          className="noFocusWithin"
          required
        >
          <Radio.Group disabled={isLocked} className="rankingTypeRadioGroup">
            {rankingTypesLoading ? (
              <Space>
                <Skeleton.Input active size="default" style={{ width: 140 }} />
                <Skeleton.Input active size="default" style={{ width: 140 }} />
              </Space>
            ) : (
              rankingTypes?.map((eachType) => (
                <Radio key={eachType.id} value={eachType.id}>
                  {eachType.type} based ranking
                </Radio>
              ))
            )}
          </Radio.Group>
        </Form.Item>

        {watchRankingTypeId === 1 && (
          <Form.Item
            name="ranking_template_id"
            label="Select Template"
            // FR-013: required on create only — not persisted/restored on Edit.
            rules={
              isEdit
                ? []
                : [
                    {
                      required: true,
                      message: "A ranking template must be selected.",
                    },
                  ]
            }
            // Suppresses Select's raw-id fallback text while loading or if the template was deleted.
            getValueProps={(value) => ({
              value:
                isFirstTemplateOptionsLoad || isDeletedTemplateReference
                  ? undefined
                  : value,
            })}
          >
            <Select
              aria-label="Select Template"
              placeholder={
                isFirstTemplateOptionsLoad
                  ? "Loading templates..."
                  : isDeletedTemplateReference
                    ? "The original template is no longer available."
                    : "Select Template"
              }
              loading={isFirstTemplateOptionsLoad}
              disabled={isLocked}
              onChange={onTemplateSelect}
              options={rankingTemplateOptions?.map((eachTemplate) => ({
                label: eachTemplate.title,
                value: eachTemplate.id,
              }))}
            />
          </Form.Item>
        )}

        {watchRankingTypeId === 1 && (
          <Form.Item
            className="rankProjectFormItems"
            rules={[
              {
                validator: async (_, ranks) => {
                  if (!ranks || ranks.length < 1) {
                    return Promise.reject(
                      new Error("At least one ranking option is required."),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Form.List name="ranks">
              {(fields, { remove }) => {
                // Only show a placeholder icon when at least one rank in the list has one.
                const hasAnyIcon = fields.some(
                  ({ name }) => !!watchRanks?.[name]?.rank_icon_url,
                );
                return (
                  <Table
                    className="rankTable"
                    size="small"
                    pagination={false}
                    rowKey="key"
                    dataSource={fields.map(({ key, name }) => ({
                      key,
                      name,
                      rank: watchRanks?.[name],
                    }))}
                    columns={[
                      {
                        title: "Ranks",
                        dataIndex: "rank",
                        render: (_, record) => (
                          <Space align="center" className="rankNameWrap">
                            {hasAnyIcon &&
                              (record.rank?.rank_icon_url ? (
                                <img
                                  src={`${process.env.REACT_APP_RANKING_ICON_URL}/${record.rank.rank_icon_url}`}
                                  alt="Rank icon"
                                  className="rankTableIcon"
                                />
                              ) : (
                                <em className="defaultImg">Icon</em>
                              ))}
                            <Typography.Text
                              className="rankNameText"
                              ellipsis={{ tooltip: record.rank?.rank_name }}
                            >
                              {record.rank?.rank_name}
                            </Typography.Text>
                          </Space>
                        ),
                      },
                      {
                        title: "",
                        key: "action",
                        width: 60,
                        align: "right",
                        render: (_, record) =>
                          !isLocked &&
                          fields.length > 1 && (
                            <Button
                              onClick={() => remove(record.name)}
                              aria-label={`Remove ${record.rank?.rank_name ?? "rank"}`}
                              icon={<DeleteOutlined aria-hidden />}
                              className="noPaddingBtn"
                            />
                          ),
                      },
                    ]}
                  />
                );
              }}
            </Form.List>
          </Form.Item>
        )}
      </div>
      <div className="blkFooter">
        <Form.Item>
          <Space>
            <Button
              htmlType="button"
              onClick={() => setRankingProjectDrawerState(false)}
            >
              Cancel
            </Button>
            <Form.Item shouldUpdate>
              {({ isFieldsTouched, getFieldsError }) => {
                const isTitleTouched = isFieldsTouched(["title"]);
                return (
                  <Button
                    role="button"
                    type="primary"
                    htmlType="submit"
                    // Don't allow submitting while dropdown data is still loading.
                    disabled={Boolean(
                      !isTitleTouched ||
                      getFieldsError().filter(({ errors }) => errors.length)
                        .length ||
                      rankingTypesLoading ||
                      rankingTemplateOptionsLoading,
                    )}
                  >
                    {rankingProjectDrawerOptions.submitBtnLabel}
                  </Button>
                );
              }}
            </Form.Item>
          </Space>
        </Form.Item>
      </div>
    </Form>
  );
};

const OrgCreateEditRankingProject: React.FC<{}> = () => {
  const [visibility, setVisibility] = useState<boolean>(false);
  const {
    rankingProjectDrawerState,
    rankingProjectDrawerOptions,
    formLoader,
    resetDrawerState,
    setRankingProjectDrawerState,
  } = useRankingProjectListStore();

  const afterOpenChange = (visible: boolean) => {
    setVisibility(visible);
    if (!visible) {
      resetDrawerState();
      // Blurs the trigger button AntD refocuses on close, else its focus ring stays visible.
      setTimeout(() => {
        (document.activeElement as HTMLElement | null)?.blur();
      }, 10);
    }
  };

  return (
    <Drawer
      title={<h2>{`${rankingProjectDrawerOptions.title}`}</h2>}
      maskClosable={false}
      destroyOnClose={true}
      className="fixedFooterBtns"
      width={720}
      closable={false}
      onClose={() => setRankingProjectDrawerState(false)}
      open={rankingProjectDrawerState}
      afterOpenChange={afterOpenChange}
      extra={
        <Button
          onClick={() => setRankingProjectDrawerState(false)}
          className="closeBlk"
        >
          Close
        </Button>
      }
    >
      <Spin spinning={formLoader}>
        <CreateEditRankingProjectForm visibility={visibility} />
      </Spin>
    </Drawer>
  );
};

export default OrgCreateEditRankingProject;
